import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import queryString from 'utils/queryString';
import { useRouter } from 'next/router';
import { Anchor, Alert, Card, Container, Spinner, Typography } from 'components/base';
import { InstagramButton } from 'components/base/social';
import useUserContext from 'ecommerce/context/user';

import SETTINGS from '../settings';
import useIGFetch from './useIGFetch';
import generateYearBookData from '../generator/generateYearBookData';
import { getMaximumNumberOfPhotos, getMinimumNumberOfPhotos } from 'editor-utils/template';

import InstagramPhotoList, { parseTimestamp } from './instagramPhotoList';
import useEditorContext from '../context/editorContext';
import pagesValidator from '../lib/pagesValidator';
import { formatData } from 'editor-utils/dataParser';

const CURRENT_YEAR = 2021;

const InstagramEditor = props => {
  const router = useRouter();
  const userContext = useUserContext();
  const editorContext = useEditorContext();

  const { photos } = props.item;

  const showAll = router.query.showall === '';

  const [instagramToken, setInstagramToken] = useState({ loading: true });
  const [mediaLoading, mediaError, instagramMedia] = useIGFetch(
    'me/media?fields=id,media_url,media_type,timestamp',
    instagramToken.value,
    true,
    showAll ? 0 : CURRENT_YEAR,
  );
  const [profileLoading, profileError, instagramProfile] = useIGFetch(
    'me?fields=id,username',
    instagramToken.value,
    false,
  );

  const minimumNumberOfPhotos = getMinimumNumberOfPhotos(props.template) - 1;
  const maximumNumberOfPhotos = getMaximumNumberOfPhotos(props.template) - 1;

  useEffect(() => {
    if (!userContext.isLoading) {
      const instagramTokens = userContext.data.instagram_tokens ?? [];

      if (instagramTokens.length > 0) {
        setInstagramToken({ value: instagramTokens[instagramTokens.length - 1] });
      } else {
        setInstagramToken({ required: true });
      }
    }
  }, [userContext.isLoading]);

  useEffect(() => {
    if (!mediaLoading && !mediaError) {
      let selected = false;
      if (instagramMedia.length <= maximumNumberOfPhotos) {
        selected = true;
      }

      editorContext.setPhotos(
        props.item.id,
        instagramMedia.map(photo => ({
          key: photo.id,
          url: photo.media_url,
          datetime: photo.timestamp,
          alive: 1,
          selected,
          // for unselected photo, set some invalid value - we don't need to call image proxy or filestack
          width: selected ? undefined : -1,
          height: selected ? undefined : -1,
          validDomain: selected ? undefined : true,
        })),
      );
    }
  }, [mediaLoading, mediaError, instagramMedia]);

  const handleOnConnectToInstagram = e => {
    e.preventDefault();

    const instagramUrl = [
      'https://api.instagram.com/oauth/authorize',
      queryString.stringify({
        client_id: SETTINGS.INSTAGRAM_APP_ID,
        redirect_uri: SETTINGS.INSTAGRAM_REDIRECT_URI,
        scope: 'user_profile,user_media',
        response_type: 'code',
        state: JSON.stringify({
          url: router.asPath,
        }),
      }),
    ].join('?');

    window.location.href = instagramUrl;
  };

  const handleOnAddToCartClick = async coverKey => {
    const sortedPhotos = [...photos.filter(photo => photo.selected)];
    sortedPhotos.sort((a, b) => parseTimestamp(a.datetime) - parseTimestamp(b.datetime));

    const coverPhoto = photos.find(photo => photo.key === coverKey) ?? sortedPhotos[0];

    const { data: generatedData } = await generateYearBookData(
      props.template,
      coverPhoto,
      sortedPhotos,
      `@${instagramProfile.username}`,
    );

    const validator = pagesValidator(props.template, generatedData.pages, props.config);
    const data = formatData(props.template, generatedData, { validator });

    if (validator.isValid()) {
      props.onFinish(data);
      return;
    }

    const errors = validator.getErrors();
    if (errors.length > 0) {
      editorContext.toast(errors[0].text, {
        type: 'warning',
      });
    }
  };

  if (instagramToken.required || mediaError || profileError) {
    return (
      <Container>
        <Card className="text-center mx-auto mw-480 mb-5">
          <Card.Body>
            <Typography.H4 className=" text-warning">CONNECT TO INSTAGRAM</Typography.H4>
            <Typography.P className="mb-2">
              Simply connect, so we can access your photos.
              <br />
              Then, add your Yearbook to your cart.
            </Typography.P>

            <InstagramButton onClick={handleOnConnectToInstagram}>
              <span>Connect to Instagram</span>
            </InstagramButton>
          </Card.Body>
        </Card>

        {(mediaError || profileError) && (
          <Alert type="danger" className="mx-auto mw-660 mb-5">
            Oh no! We’re sorry - Instagram is having a hard time connecting your account at this
            time. Please come back and try again in a few minutes. If you are still having troubles,
            please email help@parabo.press.
          </Alert>
        )}
      </Container>
    );
  }

  if (instagramToken.loading || mediaLoading || profileLoading) {
    return (
      <Container>
        <div className="text-center" style={{ minHeight: 400 }}>
          <Spinner />
        </div>
      </Container>
    );
  }

  const message = mediaCount => {
    if (mediaCount < minimumNumberOfPhotos) {
      return (
        <>
          <Typography.P className="mb-2">
            Oh no! You only have {mediaCount} photo(s) from the year {CURRENT_YEAR}. To create an
            Instagram Yearbook, we need at least {minimumNumberOfPhotos} photos from your feed.
            <br />
            <br />
            You can either connect a different account or try our{' '}
            <Link href="/[...params]" as="/softcover-books" legacyBehavior>
              <Anchor>Softcover Book</Anchor>
            </Link>
            .
          </Typography.P>
          <InstagramButton onClick={handleOnConnectToInstagram}>
            <span>Connect to Instagram</span>
          </InstagramButton>
        </>
      );
    }

    return (
      <Typography.P className="mb-0">
        Your {mediaCount} Instagram photos have finished uploading. <br />
        Select {minimumNumberOfPhotos} photos for your book, and click Cover on the photo you’d like
        on the front.
        <br />
        You can add extra photos beyond {minimumNumberOfPhotos} for a small fee.
      </Typography.P>
    );
  };

  const selectedNumberOfPhotos = photos.filter(photo => photo.selected).length;

  return (
    <Container>
      <Card className="text-center mx-auto mw-480 mb-5">
        <Card.Body>
          <Typography.H4 className="text-warning">@{instagramProfile.username}</Typography.H4>
          {message(photos.length)}
        </Card.Body>
      </Card>

      {photos.length >= minimumNumberOfPhotos && (
        <InstagramPhotoList
          id={props.item.id}
          photos={photos}
          year={CURRENT_YEAR}
          selectedNumberOfPhotos={selectedNumberOfPhotos}
          minimumNumberOfPhotos={minimumNumberOfPhotos}
          maximumNumberOfPhotos={maximumNumberOfPhotos}
          onTogglePhoto={(key, selected) => {
            editorContext.updatePhoto(props.item.id, key, value => draft => {
              draft.selected = selected;

              if (selected) {
                // when user selects photo, clear photos size and validDomain to refetch
                draft.validDomain = undefined;
                if (!(value.width > 0) || !(value.height > 0)) {
                  draft.width = undefined;
                  draft.height = undefined;
                }
              }
            });
          }}
          onSuccess={handleOnAddToCartClick}
        />
      )}
    </Container>
  );
};

InstagramEditor.propTypes = {
  item: PropTypes.object,
  config: PropTypes.object,
  product: PropTypes.object,
  template: PropTypes.object,
  onFinish: PropTypes.func,
};

export default InstagramEditor;
